import { useEffect, useState, type ReactNode } from "react";
import {
  Alert,
  ApiError,
  Button,
  BypassOtpBanner,
  OtpChannelPicker,
  OtpInput,
  apiFetch,
  otpSentMessage,
  type OtpChannel,
  type PortalLoginResendRequest,
  type PortalLoginVerifyRequest,
  type PortalLoginVerifyResponse,
} from "@dearbill/shared";

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "";

/** Resend cooldown, mirroring `SEND_COOLDOWN_SECONDS` in server/app/services/rate_limit.py. */
const RESEND_COOLDOWN_S = 30;

interface PortalOtpProps {
  /** Short-lived challenge token from the login step. */
  challenge: string;
  /** `bypassSmsOtp` from the login response; the bypass banner shows when it is true. */
  bypassSmsOtp: boolean;
  /** Last 4 digits of the phone the code will be sent to, for the prompt copy. */
  phoneHint?: string | undefined;
  /** Called with the bearer `session` once the OTP is accepted. */
  onAuthenticated: (session: string) => void;
  /**
   * Return to the login form, clearing the challenge, with an optional message
   * to show there. Back/edit and the forced-restart error paths (max attempts,
   * expired challenge) use it.
   */
  onRestart: (message?: string) => void;
}

/**
 * Patient-portal OTP step, step 2 of the OTP-gated login, shown when there's a
 * `challenge` but no session yet. Login sends no code: the user picks a
 * delivery channel (text message, preselected, or voice call) and presses
 * "Send code". `/api/portal/login/resend` is the only send path, for the first
 * send and every resend, so the cooldown starts at 0 and begins on the first
 * successful send or a 429. The code input appears only after a send,
 * including a 429 from a quick re-login hitting the shared cooldown, because a
 * code sent within the cooldown is still valid and can be typed without
 * re-sending. The 6-digit code auto-submits to `/api/portal/login/verify` once
 * complete. Back/edit returns to the form.
 */
export function PortalOtp({
  challenge,
  bypassSmsOtp,
  phoneHint,
  onAuthenticated,
  onRestart,
}: PortalOtpProps): ReactNode {
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  // Delivery choice for the next send; the picker stays visible after sending,
  // so a resend can switch channels.
  const [channel, setChannel] = useState<OtpChannel>("sms");
  // Channel of the last successful send, null until the user sends a code. It
  // drives the pre-send or channel-aware post-send copy and the button label.
  const [sentVia, setSentVia] = useState<OtpChannel | null>(null);
  // True once a code is probably out there; it gates the code input. It is set
  // on a successful send and on a 429, when a code sent within the cooldown is
  // still valid. `sentVia` stays null on a 429, so the copy doesn't claim a send.
  const [sent, setSent] = useState(false);
  // Seconds left in the send cooldown: 0 on mount, because login sends nothing,
  // and `RESEND_COOLDOWN_S` after a successful send or a 429.
  const [cooldown, setCooldown] = useState(0);

  // Tick the cooldown down to 0 once per second. Synchronization with the
  // countdown value, so this is a legitimate effect (not an event).
  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => setCooldown((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  const submit = (value: string): void => {
    setBusy(true);
    setError(null);
    const body: PortalLoginVerifyRequest = { challenge, code: value };
    apiFetch<PortalLoginVerifyResponse>(
      "/api/portal/login/verify",
      { method: "POST", body: body as unknown as Record<string, unknown> },
      { baseUrl: API_BASE },
    )
      .then((res) => {
        if (res.session) {
          onAuthenticated(res.session);
          return;
        }
        // A 200 with no session: branch on the reason code.
        setBusy(false);
        setCode("");
        if (res.reason === "rejected") {
          setError("Incorrect code, try again.");
        } else if (res.reason === "expired") {
          setError("Code expired — send a new one.");
        } else if (res.reason === "max_attempts_exceeded") {
          onRestart("Too many incorrect attempts. Please start over.");
        } else {
          setError("Something went wrong. Please try again.");
        }
      })
      .catch((err: unknown) => {
        setBusy(false);
        setCode("");
        // A bad or expired challenge (not a bad code) is a 401 and forces a restart.
        if (err instanceof ApiError && err.status === 401) {
          onRestart("Your session expired. Please start over.");
          return;
        }
        setError("Something went wrong. Please try again.");
      });
  };

  const onSend = (): void => {
    if (sending || cooldown > 0) return;
    setSending(true);
    setError(null);
    const body: PortalLoginResendRequest = { challenge, channel };
    apiFetch<{ status: string }>(
      "/api/portal/login/resend",
      { method: "POST", body: body as unknown as Record<string, unknown> },
      { baseUrl: API_BASE },
    )
      .then(() => {
        setSending(false);
        setSent(true);
        setSentVia(channel);
        setCooldown(RESEND_COOLDOWN_S);
      })
      .catch((err: unknown) => {
        setSending(false);
        if (err instanceof ApiError) {
          if (err.status === 429) {
            // The server-side cooldown is active, so a recent code is still
            // valid: reveal the input and restart the countdown, but don't
            // claim a send.
            setSent(true);
            setCooldown(RESEND_COOLDOWN_S);
            return;
          }
          if (err.status === 401) {
            onRestart("Your session expired. Please start over.");
            return;
          }
        }
        setError("Couldn't send the code. Please try again.");
      });
  };

  const maskedPhone = phoneHint ? `***${phoneHint}` : undefined;

  return (
    <div>
      <h1 className="text-h3 text-midnight font-serif">Enter your verification code</h1>
      <p className="text-body-sm text-slate-teal mt-2">
        {sentVia
          ? `${otpSentMessage(sentVia, maskedPhone)} Enter it below to continue.`
          : "Choose how you'd like to get your 6-digit code, then enter it below to continue."}
      </p>

      <div className="mt-6 space-y-5">
        <BypassOtpBanner bypassSmsOtp={bypassSmsOtp} />

        <OtpChannelPicker
          legend={maskedPhone ? `How should we send your code to ${maskedPhone}?` : undefined}
          value={channel}
          onChange={setChannel}
          disabled={sending}
        />

        {sent && (
          <>
            <OtpInput
              value={code}
              onChange={(v) => {
                setCode(v);
                if (error) setError(null);
              }}
              onComplete={submit}
              disabled={busy}
              error={error !== null}
            />

            {busy && <p className="text-caption text-slate-teal">Verifying…</p>}
          </>
        )}

        {error && <Alert tone="error" title={error} />}

        <div className="flex items-center justify-between">
          <Button type="button" variant="ghost" onClick={() => onRestart()}>
            Back / edit info
          </Button>
          <Button
            type="button"
            variant={sentVia ? "secondary" : "primary"}
            onClick={onSend}
            disabled={sending || cooldown > 0}
          >
            {cooldown > 0
              ? `${sentVia ? "Resend" : "Send"} code (${cooldown}s)`
              : sending
                ? "Sending…"
                : sentVia
                  ? "Resend code"
                  : "Send code"}
          </Button>
        </div>
      </div>
    </div>
  );
}
